import type { Root } from "./prototypeTypes";
import type { PrototypeApiClient } from "./apiClient";
import { renderPagedInfiniteList } from "./progressiveList";
import { buildCard, buildEmpty, renderSkeleton } from "./githubTrendingCard";
import type { GithubTrendingSegment, RepoRow, TrendingSegmentResponse } from "./githubTrendingHydratorTypes";
import { shouldCommitTextInputOnEnter } from './inputComposition';

export type { GithubTrendingSegment } from "./githubTrendingHydratorTypes";

export interface HydrateGithubTrendingOptions {
	/**
	 * P4.2: isPaid 字段已废弃 - 所有身份（含 Visitor/Free/Lite）都能看到 Trending 列表和公开摘要。
	 * Deep Dive 按钮的鉴权在 card 层处理（仅 Pro/Max 可点开，其他身份走 paywall）。
	 * 字段保留仅为向后兼容旧调用点，不再有任何效果。
	 */
	isPaid?: boolean;
	/** Outer view/panel or the `[data-gh-mount]` component itself. */
	scope?: HTMLElement;
}

interface GithubTrendingMountState {
	api: PrototypeApiClient;
	segment: GithubTrendingSegment;
	repos: RepoRow[];
}

export interface GithubFreshnessPresentation {
	hidden: boolean;
	stale: boolean;
	text: string;
}

const mountStates = new WeakMap<Element, GithubTrendingMountState>();

function resolveGithubTrendingMount(root: Root, origin?: Element | null): Element | null {
	if (origin?.matches('[data-gh-mount]')) return origin;
	const ancestor = origin?.closest('[data-gh-mount]');
	if (ancestor) return ancestor;
	const child = origin?.querySelector('[data-gh-mount]');
	if (child) return child;
	return root.querySelector('#view-github-trending.active [data-gh-mount], #view-social.active .social-panel.active [data-gh-mount]')
		?? root.querySelector('[data-gh-mount="desktop"]')
		?? root.querySelector('[data-gh-mount]');
}

function mountState(mount: Element, api?: PrototypeApiClient): GithubTrendingMountState | null {
	const current = mountStates.get(mount);
	if (current) {
		if (api) current.api = api;
		return current;
	}
	if (!api) return null;
	const created: GithubTrendingMountState = { api, segment: 'completed', repos: [] };
	mountStates.set(mount, created);
	return created;
}

function hydrationKey(mount: Element): string {
	return `github-trending:${mount.getAttribute('data-gh-mount') ?? 'default'}`;
}

export async function hydrateGithubTrending(
	root: Root,
	api: PrototypeApiClient,
	opts: HydrateGithubTrendingOptions = {},
): Promise<void> {
	// P4.2: 所有身份都能看 Trending 列表（spec §8.3）。
	// isPaid 参数保留向后兼容但忽略 - Deep Dive 按钮单独鉴权。
	void opts.isPaid;
	const scope = resolveGithubTrendingMount(root, opts.scope);
	if (!scope) return;
	const state = mountState(scope, api);
	if (!state) return;
	const gate = scope.querySelector<HTMLElement>('[data-gh-role="gate"]');
	const unlocked = scope.querySelector<HTMLElement>('[data-gh-role="unlocked"]');
	// P4.2: gate 不再阻断列表 - 隐藏 gate（订阅面板），所有身份都看 unlocked 列表
	if (gate) gate.style.display = "none";
	if (unlocked) unlocked.style.display = "block";

	// Dedupe by component instance. Desktop and Social have independent state
	// even when both fragments coexist in the document.
	const { shouldHydrate } = await import("./hydrateGuard");
	if (!shouldHydrate(hydrationKey(scope))) return;

	// The mount's active capsule is the UI truth source. It survives detail
	// navigation, while showView() can deliberately reset it to completed.
	const active = scope.querySelector<HTMLElement>('[data-gh-role="filters"] .category-pill.active');
	const segment = (active?.dataset.ghFilter ?? state.segment) as GithubTrendingSegment;
	await fetchAndRenderGithubRepos(scope, state, segment, currentGhSearch(scope));
}

async function fetchAndRenderGithubRepos(
	scope: Element,
	state: GithubTrendingMountState,
	segment: GithubTrendingSegment,
	search: string,
): Promise<void> {
	const list = scope.querySelector<HTMLElement>('[data-gh-role="repo-list"]');
	if (!list) return;
	// Completed 段使用 grid 布局 (topic 卡片), 其他段使用单列行布局 (repo 卡片)
	list.classList.toggle("gh-repo-list--grid", segment === "completed");
	const skeletonClassName = segment === 'completed' ? 'topic-card-compact-skeleton' : 'gh-repo-skeleton';
	list.innerHTML = renderSkeleton(skeletonClassName);
	paintGithubFreshness(scope, segment);
	state.repos = [];
	state.segment = segment;
	wireGithubSearch(scope, state);
	let displayRank = 0;
	renderPagedInfiniteList({
		container: list,
		loadPage: async (cursor) => {
			const resp = (await state.api.githubTrending(segment, { limit: 20, cursor, search })) as TrendingSegmentResponse;
			if (cursor === null || cursor === '0') paintGithubFreshness(scope, segment, resp);
			return { items: resp.items ?? [], hasMore: resp.hasMore, nextCursor: resp.nextCursor ?? null };
		},
		renderItem: (repo) => buildCard(repo, ++displayRank, segment),
		skeletonClassName,
		skeletonCount: 4,
		endMessage: "You've reached the end of GitHub Trending.",
		emptyElement: buildEmpty("No trending repos in this segment."),
		onItemsChange: (repos) => {
			state.repos = repos;
		},
	});
}

export function githubFreshnessPresentation(
	segment: GithubTrendingSegment,
	response?: Pick<TrendingSegmentResponse, 'stale' | 'lastCapturedAt'>,
	nowMs = Date.now(),
): GithubFreshnessPresentation {
	if (segment === 'completed' || segment === 'inprogress' || !response?.lastCapturedAt) return { hidden: true, stale: false, text: '' };
	const capturedAt = new Date(response.lastCapturedAt);
	if (!Number.isFinite(capturedAt.getTime())) return { hidden: true, stale: false, text: '' };
	const now = new Date(nowMs);
	const sameUtcDay = capturedAt.getUTCFullYear() === now.getUTCFullYear()
		&& capturedAt.getUTCMonth() === now.getUTCMonth()
		&& capturedAt.getUTCDate() === now.getUTCDate();
	const timestamp = `${capturedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}, ${String(capturedAt.getUTCHours()).padStart(2, '0')}:${String(capturedAt.getUTCMinutes()).padStart(2, '0')} UTC`;
	if (response.stale) return { hidden: false, stale: true, text: `Data may be stale · Last captured ${timestamp}` };
	return { hidden: false, stale: false, text: sameUtcDay ? `Updated today · ${timestamp}` : `Last captured ${timestamp}` };
}

function paintGithubFreshness(scope: Element, segment: GithubTrendingSegment, response?: TrendingSegmentResponse): void {
	const container = scope.querySelector<HTMLElement>('[data-gh-role="freshness"]');
	const copy = scope.querySelector<HTMLElement>('[data-gh-role="freshness-copy"]');
	if (!container || !copy) return;
	const presentation = githubFreshnessPresentation(segment, response);
	container.hidden = presentation.hidden;
	container.classList.toggle('is-stale', presentation.stale);
	copy.textContent = presentation.text;
}

/**
 * Read the current GitHub trending search input value. Centralized
 * so every entry point (initial render, capsule switch, search commit,
 * clear) agrees on what "current search" means.
 */
function currentGhSearch(scope: Element): string {
	const input = scope.querySelector<HTMLInputElement>('[data-gh-role="search-input"]');
	return (input?.value ?? "").trim();
}

function wireGithubSearch(scope: Element, state: GithubTrendingMountState): void {
	const input = scope.querySelector<HTMLInputElement>('[data-gh-role="search-input"]');
	if (!input || input.dataset.bound === "true") return;
	input.dataset.bound = "true";
	// Search is button-triggered (and Enter key) — NOT live debounce.
	// On every keystroke we only toggle the clear button visibility;
	// commit sends a fresh server request via fetchAndRenderGithubRepos
	// so pagination reflects the full result set.
	input.addEventListener("input", () => toggleGhClearButton(scope));
	input.addEventListener("keydown", (event) => {
		if (shouldCommitTextInputOnEnter(event)) {
			event.preventDefault();
			if (typeof input.blur === 'function') input.blur();
			runGhSearchInternal(scope, state);
		}
	});
}

function toggleGhClearButton(scope: Element): void {
	const input = scope.querySelector<HTMLInputElement>('[data-gh-role="search-input"]');
	const clearBtn = scope.querySelector<HTMLButtonElement>('[data-gh-role="search-clear"]');
	if (!input || !clearBtn) return;
	clearBtn.style.display = input.value.trim() ? "" : "none";
}

function runGhSearchInternal(scope: Element, state: GithubTrendingMountState): void {
	toggleGhClearButton(scope);
	void fetchAndRenderGithubRepos(scope, state, state.segment, currentGhSearch(scope));
}

/** Action: GitHub search button click. */
export function runGhSearch(root: Root, source?: Element): void {
	const scope = resolveGithubTrendingMount(root, source);
	const state = scope ? mountState(scope) : null;
	if (scope && state) runGhSearchInternal(scope, state);
}

/** Action: GitHub search clear button. */
export function clearGhSearch(root: Root, source?: Element): void {
	const scope = resolveGithubTrendingMount(root, source);
	const state = scope ? mountState(scope) : null;
	if (!scope || !state) return;
	const input = scope.querySelector<HTMLInputElement>('[data-gh-role="search-input"]');
	if (input) input.value = "";
	toggleGhClearButton(scope);
	runGhSearchInternal(scope, state);
}

/**
 * Segment capsule switch (Today / This Week / This Month / In Progress / Completed)。
 * Re-fetches from the GitHub API with the current search query so pagination
 * stays accurate across the full result set.
 */
export function applyGithubTrendingListFilter(root: Root, segment?: string, source?: Element): void {
	const scope = resolveGithubTrendingMount(root, source);
	const state = scope ? mountState(scope) : null;
	if (!scope || !state) return;
	const activeSegment = (segment ?? state.segment) as GithubTrendingSegment;
	state.segment = activeSegment;
	persistGithubTrendingSegment(activeSegment);
	void fetchAndRenderGithubRepos(scope, state, activeSegment, currentGhSearch(scope));
}

function persistGithubTrendingSegment(segment: GithubTrendingSegment): void {
	if (typeof window === 'undefined' || !window.history?.replaceState) return;
	const current = window.history.state && typeof window.history.state === 'object'
		? window.history.state as Record<string, unknown>
		: {};
	window.history.replaceState({ ...current, githubTrendingSegment: segment }, '');
}
